import { useCallback, useEffect, useRef, useState } from 'react';
import Box from '@cloudscape-design/components/box';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import {
  DiagnosticMessage,
  createDiagnosticSession,
  getDiagnosticSession,
  sendDiagnosticMessage,
} from './api';
import { aliasFromIdToken } from './auth';
import BadgePhoto from './BadgePhoto';
import './chat.css';

interface Props {
  idToken: string;
}

function formatMessageTime(ts?: string): string | null {
  if (!ts) return null;
  const date = new Date(ts);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// A short list of starter prompts to orient first-time users.
const SUGGESTIONS = [
  "Why isn't pentest AVP-12345 showing up?",
  'Why was AVP-12345 not scheduled automatically?',
  'What happened when AVP-12345 was ingested?',
];

export default function DiagnosticsPanel({ idToken }: Props) {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<DiagnosticMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const pollingAbortRef = useRef<AbortController | null>(null);
  const myAlias = aliasFromIdToken(idToken);

  // Create a fresh session on mount.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    createDiagnosticSession(idToken)
      .then((s) => {
        if (!cancelled) setSessionId(s.sessionId);
      })
      .catch(() => {
        if (!cancelled) setError('Could not start a Help Me session. Please refresh and try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      pollingAbortRef.current?.abort();
    };
  }, [idToken]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages, sending]);

  const appendMessage = (msg: DiagnosticMessage) => {
    setMessages((prev) => [...prev, { ...msg, ts: new Date().toISOString() }]);
  };

  const pollForResponse = useCallback(
    async (sid: string, initialMessageCount: number) => {
      const maxAttempts = 80; // 80 × 3s = 4 minutes (diagnostic turns can be slow)
      const interval = 3000;
      // initial (before) + 1 (user msg we sent) + 1+ (assistant response)
      const expectedMinimum = initialMessageCount + 2;

      const controller = new AbortController();
      pollingAbortRef.current = controller;

      const abortableSleep = () =>
        new Promise<void>((resolve) => {
          const timer = setTimeout(resolve, interval);
          controller.signal.addEventListener('abort', () => { clearTimeout(timer); resolve(); }, { once: true });
        });

      for (let i = 0; i < maxAttempts; i++) {
        if (controller.signal.aborted) return;
        await abortableSleep();
        if (controller.signal.aborted) return;
        try {
          const detail = await getDiagnosticSession(idToken, sid);
          if (detail.messages.length >= expectedMinimum) {
            setMessages(detail.messages);
            return;
          }
        } catch {
          // Polling failure — keep trying.
        }
      }
      appendMessage({
        role: 'assistant',
        content: '⚠️ This is taking longer than expected. Please refresh to check for updates.',
      });
    },
    [idToken],
  );

  const handleSendText = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || sending || !sessionId) return;
      const countBeforeSend = messages.length;
      setInput('');
      setSending(true);
      appendMessage({ role: 'user', content: trimmed, sender: myAlias });
      try {
        const res = await sendDiagnosticMessage(idToken, sessionId, trimmed);
        if (res.status === 'processing') {
          await pollForResponse(sessionId, countBeforeSend);
        }
      } catch {
        appendMessage({ role: 'assistant', content: '⚠️ Failed to send your question. Please try again.' });
      } finally {
        setSending(false);
      }
    },
    [idToken, messages.length, myAlias, pollForResponse, sending, sessionId],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSendText(input);
    }
  };

  if (error) {
    return (
      <Container>
        <Box color="text-status-error" padding="l">{error}</Box>
      </Container>
    );
  }

  return (
    <Container
      header={
        <Header
          variant="h2"
          description="Ask why a pentest behaved the way it did. I can read ASPEN's CloudWatch logs and source code to explain — I can't change anything."
        >
          🩺 Help Me — Diagnostics
        </Header>
      }
    >
      <SpaceBetween size="s">
        <div className="chat-container">
          {loading ? (
            <Box textAlign="center" padding="xl"><Spinner size="large" /></Box>
          ) : (
            <div className="chat-messages">
              {messages.length === 0 && !sending && (
                <Box color="text-body-secondary" padding="s">
                  <SpaceBetween size="xs">
                    <div>Ask me about a specific pentest by its AVP id. For example:</div>
                    {SUGGESTIONS.map((s) => (
                      <button
                        key={s}
                        className="chat-approve-btn"
                        style={{ textAlign: 'left' }}
                        onClick={() => void handleSendText(s)}
                        disabled={sending}
                      >
                        {s}
                      </button>
                    ))}
                  </SpaceBetween>
                </Box>
              )}
              {messages.map((msg, i) => (
                <div key={i} className={`chat-bubble chat-bubble--${msg.role}`}>
                  <div className="chat-bubble__label">
                    {msg.role === 'user' && msg.sender && msg.sender !== 'ASPEN' && (
                      <BadgePhoto alias={msg.sender} />
                    )}
                    {msg.role === 'user' ? (msg.sender || 'You') : 'ASPEN'}
                    {formatMessageTime(msg.ts) && (
                      <span className="chat-bubble__time">{formatMessageTime(msg.ts)}</span>
                    )}
                  </div>
                  <div className="chat-bubble__content">
                    <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>{msg.content}</Markdown>
                  </div>
                </div>
              ))}
              {sending && (
                <div className="chat-bubble chat-bubble--assistant">
                  <div className="chat-bubble__label">ASPEN</div>
                  <div className="chat-bubble__content chat-typing">
                    <span></span><span></span><span></span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {!loading && (
          <div className="chat-input-row">
            <textarea
              className="chat-input"
              placeholder="Ask why a pentest isn't showing (e.g. 'why isn't AVP-12345 showing?')..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={sending || !sessionId}
              rows={2}
            />
            <button
              className={`chat-send-btn ${sending ? 'chat-send-btn--disabled' : ''}`}
              onClick={() => void handleSendText(input)}
              disabled={sending || !sessionId || !input.trim()}
              aria-label="Send message"
            >
              <svg viewBox="0 0 24 24" width="22" height="22">
                <path d="M2 21l21-9L2 3v7l15 2-15 2z" fill="currentColor" />
              </svg>
            </button>
          </div>
        )}
      </SpaceBetween>
    </Container>
  );
}
